import {
  CalendarPlus,
  EnvelopeSimple,
  PencilSimple,
  Phone,
  Trash,
  UserCircleMinus,
  WarningCircle,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ContactDrawer } from '../components/contact-drawer/contact-drawer';
import { ContactActivity } from '../components/contact-activity/contact-activity';
import { ContactSubscriptionPanel } from '../components/contact-subscription-panel';
import { ContactSubscriptionStatusPill } from '../components/contact-subscription-status-pill/contact-subscription-status-pill';
import { useDeleteContact, useGetContactDetails } from '../hooks/use-contacts';
import { useCustomFieldDefinitions } from '../hooks/use-custom-field-definitions';
import { getLanguageLabel } from '../types/contacts-filters.types';
import type { Contact } from '../types/contacts.types';
import {
  getContactDisplayName,
  getContactInitials,
  getContactLists,
  toContactStringValue,
} from '../utils/contact-attributes';
import { formatContactSourceLabel } from '../utils/contact-source.utils';
import { getContactAdditionalAttributesForDetails } from '../utils/custom-field-attributes.utils';
import { isContactNotFoundError } from '../utils/contact-error.utils';
import { getEmailChannelStatus } from '../utils/contact-subscription-status';

const DETAIL_TABS = ['Profile', 'Activity'] as const;
type DetailTab = (typeof DETAIL_TABS)[number];

const AVATAR_BACKGROUNDS = ['#0066B2', '#1F8A46', '#B4690E', '#791E94', '#005494'];

const formatCreatedLabel = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const getAvatarBackground = (contact: Contact): string => {
  const index = contact.ItemId.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return AVATAR_BACKGROUNDS[index % AVATAR_BACKGROUNDS.length];
};

const DetailFieldRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <div>
    <div className="mb-1 text-xs text-neutral-500">{label}</div>
    <div className="text-sm text-neutral-900">{value}</div>
  </div>
);

const DetailCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
    <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">{title}</div>
    {children}
  </div>
);

const ContactDetailsLoading = () => (
  <div className="flex w-full max-w-[1200px] flex-col gap-5">
    <div className="flex items-center gap-4 rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
      <Skeleton className="size-14 rounded-full" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-[18px] w-[180px]" />
        <Skeleton className="h-[14px] w-[220px]" />
      </div>
    </div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <div className="flex flex-col gap-5 lg:col-span-2">
        <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
          <Skeleton className="mb-3 h-[14px] w-[140px]" />
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
        <Skeleton className="mb-3 h-[14px] w-[160px]" />
        <Skeleton className="h-8 w-full" />
      </div>
    </div>
  </div>
);

const ContactDetailsNotFound = ({ onBack }: { onBack: () => void }) => (
  <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border border-neutral-200 bg-white py-16 shadow-e1">
    <div className="grid size-14 place-items-center rounded-full bg-neutral-100">
      <UserCircleMinus className="size-[26px] text-neutral-400" aria-hidden />
    </div>
    <h2 className="text-lg font-semibold text-neutral-900">Contact not found</h2>
    <p className="max-w-[360px] text-center text-xs text-neutral-500">
      This contact may have been deleted, or the link is out of date.
    </p>
    <Button type="button" className="mt-1 bg-primary-600 hover:bg-primary-700" onClick={onBack}>
      Back to Contacts
    </Button>
  </div>
);

const ContactDetailsError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border border-neutral-200 bg-white py-16 shadow-e1">
    <div className="grid size-14 place-items-center rounded-full bg-error-background">
      <WarningCircle className="size-[26px] text-error" weight="regular" aria-hidden />
    </div>
    <h2 className="text-lg font-semibold text-neutral-900">Couldn&apos;t load this contact</h2>
    <p className="max-w-[360px] text-center text-xs text-neutral-500">
      Something went wrong on our end. Try again in a moment.
    </p>
    <Button type="button" className="mt-1 bg-primary-600 hover:bg-primary-700" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

export const ContactDetailsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { contactId = '' } = useParams();
  const [activeTab, setActiveTab] = useState<DetailTab>('Profile');
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { mutateAsync: deleteContact, isPending: isDeletingContact } = useDeleteContact();
  const { contact, isLoading, isError, error, refetch } = useGetContactDetails(contactId);

  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();
  const additionalAttributes = useMemo(
    () => getContactAdditionalAttributesForDetails(contact, customFieldDefinitionsData?.items),
    [contact, customFieldDefinitionsData?.items]
  );

  const handleDeleteContact = async () => {
    if (!contact) return;

    try {
      await deleteContact({ contactId: contact.ItemId });
      toast({
        variant: 'success',
        title: 'Contact deleted',
        description: `${getContactDisplayName(contact)} was deleted.`,
      });
      setIsDeleteConfirmOpen(false);
      navigate('/contacts');
    } catch (deleteError) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete contact',
        description: deleteError instanceof Error ? deleteError.message : 'Please try again.',
      });
    }
  };

  if (isLoading) {
    return <ContactDetailsLoading />;
  }

  if (isError) {
    if (isContactNotFoundError(error)) {
      return <ContactDetailsNotFound onBack={() => navigate('/contacts')} />;
    }

    return <ContactDetailsError onRetry={() => void refetch()} />;
  }

  if (!contact) {
    return <ContactDetailsNotFound onBack={() => navigate('/contacts')} />;
  }

  const fullName = getContactDisplayName(contact);
  const email = contact.Email || '—';
  const phone = toContactStringValue(contact.Phone) || '—';
  const firstName = toContactStringValue(contact.FirstName) || '—';
  const lastName = toContactStringValue(contact.LastName) || '—';
  const locale =
    toContactStringValue(contact.Locale) ||
    (contact.Language ? getLanguageLabel(contact.Language) : undefined) ||
    '—';
  const sourceLabel = formatContactSourceLabel(contact) || '—';
  const lists = getContactLists(contact);
  const emailStatus = getEmailChannelStatus(contact);
  const createdLabel = formatCreatedLabel(contact.CreatedDate);

  return (
    <div className="flex w-full max-w-[1200px] flex-col gap-5">
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
        <span
          className="grid size-14 shrink-0 place-items-center rounded-full text-lg font-semibold text-white"
          style={{ backgroundColor: getAvatarBackground(contact) }}
          aria-hidden
        >
          {getContactInitials(contact)}
        </span>

        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-neutral-900">{fullName}</h1>
            {emailStatus ? <ContactSubscriptionStatusPill status={emailStatus} /> : null}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-600">
            <span className="inline-flex items-center gap-1.5">
              <EnvelopeSimple className="size-3.5 text-neutral-400" aria-hidden />
              {email}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Phone className="size-3.5 text-neutral-400" aria-hidden />
              {phone}
            </span>
            <span className="inline-flex items-center gap-1.5 text-neutral-500">
              <CalendarPlus className="size-3.5 text-neutral-400" aria-hidden />
              Created {createdLabel}
            </span>
          </div>
        </div>

        <div className="flex-1" />

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-10 border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-50"
            onClick={() => setIsEditContactOpen(true)}
          >
            <PencilSimple className="size-4" aria-hidden />
            Edit
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-10 text-error hover:bg-error-background/40 hover:text-error"
            onClick={() => setIsDeleteConfirmOpen(true)}
          >
            <Trash className="size-4" aria-hidden />
            Delete
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-neutral-200">
        {DETAIL_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={cn(
              '-mb-px border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
              activeTab === tab
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-900'
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Profile' ? (
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-3">
          <div className="flex flex-col gap-5 lg:col-span-2">
            <DetailCard title="Standard fields">
              <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
                <DetailFieldRow label="Email" value={email} />
                <DetailFieldRow label="Phone" value={phone} />
                <DetailFieldRow label="First name" value={firstName} />
                <DetailFieldRow label="Last name" value={lastName} />
                <DetailFieldRow label="Locale" value={locale} />
                <DetailFieldRow
                  label={
                    <>
                      Source{' '}
                      <span className="text-[11px] font-normal text-neutral-400">(read-only)</span>
                    </>
                  }
                  value={sourceLabel}
                />
              </div>
            </DetailCard>

            <DetailCard title="Custom attributes">
              <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
                {additionalAttributes.length > 0 ? (
                  additionalAttributes.map(({ definition, value }) => (
                    <DetailFieldRow
                      key={definition.Key}
                      label={definition.Label}
                      value={value ?? '—'}
                    />
                  ))
                ) : (
                  <p className="text-xs text-neutral-500 sm:col-span-2">No custom attributes.</p>
                )}
              </div>
            </DetailCard>

            <DetailCard title="Lists">
              {lists.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {lists.map((list) => (
                    <span
                      key={list}
                      className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-900"
                    >
                      {list}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-neutral-500">Not on any lists.</p>
              )}
            </DetailCard>
          </div>

          <ContactSubscriptionPanel contact={contact} />
        </div>
      ) : null}

      {activeTab === 'Activity' ? <ContactActivity contactId={contact.ItemId} /> : null}

      <ContactDrawer
        open={isEditContactOpen}
        onOpenChange={setIsEditContactOpen}
        contact={contact}
      />

      <ConfirmationModal
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (isDeletingContact) return;
          setIsDeleteConfirmOpen(open);
        }}
        title={`Delete ${fullName}?`}
        description="This removes the contact from your audience, including their lists and subscription history. This can't be undone."
        confirmText={isDeletingContact ? 'Deleting…' : 'Delete contact'}
        cancelText="Cancel"
        confirmClassName="bg-destructive text-white"
        preventAutoClose
        onConfirm={() => {
          void handleDeleteContact();
        }}
      />
    </div>
  );
};
